import { useRef, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  h1 {
    align-self: left;
    font-size: 30px;
    color: ${(props) => props.theme.colors.primaryText};
    margin-left: 30px;
    margin-bottom: 0px;
    font-weight: bold;

    /* Tablet responsive */
    @media (max-width: 768px) {
      font-size: 24px;
      margin-left: 20px;
      margin-bottom: 10px;
    }

    /* Mobile responsive */
    @media (max-width: 480px) {
      font-size: 20px;
      margin-left: 15px;
      margin-bottom: 8px;
    }
  }
`;

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  width: 95%;
  margin: auto;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 0;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    gap: 15px;
    padding: 15px 0;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    gap: 10px;
    padding: 10px 0;
  }
`;

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.8);
  color: ${(props) => props.theme.colors.primaryText};
  border: none;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  ${(props) => (props.left ? 'left: 10px;' : 'right: 10px;')}
  margin: auto;
  opacity: 0;
  pointer-events: none;

  ${Container}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: rgba(255, 255, 255, 1);
    color: black;
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${(props) => (props.left ? 'left: 5px;' : 'right: 5px;')}
    opacity: 1;
    pointer-events: auto;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    ${(props) => (props.left ? 'left: 2px;' : 'right: 2px;')}
    opacity: 1;
    pointer-events: auto;
  }
`;

// 슬라이더 컴포넌트
const SliderView = ({ title, children, style }) => {
  const sliderRef = useRef(null);
  const [slideWidth, setSlideWidth] = useState(0);

  const updateSlideWidth = () => {
    if (!sliderRef.current) return;
    const firstSlide = sliderRef.current.children[0];
    if (firstSlide) setSlideWidth(firstSlide.clientWidth + 20); // 슬라이드 크기 + gap
  };

  const handlePrev = () => {
    if (!sliderRef.current) return;

    const currentScroll = sliderRef.current.scrollLeft;
    const itemsPerMove = 5;
    const scrollAmount = slideWidth * itemsPerMove;

    // Calculate how many items can actually be scrolled back
    const actualScrollAmount = Math.min(scrollAmount, currentScroll);

    sliderRef.current.scrollLeft -= actualScrollAmount;
  };

  const handleNext = () => {
    if (!sliderRef.current) return;

    const currentScroll = sliderRef.current.scrollLeft;
    const maxScroll =
      sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
    const remainingScroll = maxScroll - currentScroll;
    const itemsPerMove = 5;
    const scrollAmount = slideWidth * itemsPerMove;

    // Calculate how much to scroll (either 5 items or remaining items if less than 5)
    const actualScrollAmount = Math.min(scrollAmount, remainingScroll);

    sliderRef.current.scrollLeft += actualScrollAmount;
  };

  return (
    <Container id={title} onMouseEnter={updateSlideWidth} style={style}>
      {title ? <h1>{title}</h1> : null}
      <SliderContainer>
        <Arrow left onClick={handlePrev}>
          &#10094;
        </Arrow>
        <ProductContainer id={title} ref={sliderRef}>
          {children}
        </ProductContainer>
        <Arrow onClick={handleNext}>&#10095;</Arrow>
      </SliderContainer>
    </Container>
  );
};

export default SliderView;
